import { Option } from 'module/ui/components/atoms/select';
import { PopoverSelect } from 'module/ui/components/molecules/popover-select';
import { PopoverSelectProps } from 'module/ui/components/molecules/popover-select/popover';
import { SingleLineEllipsis } from 'module/ui/components/styles';
import { PageTemplateLite, PageType } from 'api/page-template/types';
import { keyBy, snakeCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const TriggerContent = styled.div`
  color: var(--text-placeholder);
  ${SingleLineEllipsis};
`;

type TemplateByPage = {
  [key in PageType]: PageTemplateLite[];
};
interface Props
  extends Omit<
    PopoverSelectProps,
    'options' | 'value' | 'onChange' | 'isMulti' | 'grouped'
  > {
  templatesByPage: Partial<TemplateByPage>;
  value?: PageTemplateLite[];
  onChange?: (value: PageTemplateLite[]) => void;
}
const templateAsOption = (template: PageTemplateLite) => ({
  label: template.templateName,
  value: template.templateId,
});

export const PageTemplateSourceSelector = ({
  templatesByPage,
  value: templates,
  onChange: onTemplatesChange,
  ...restProps
}: Props) => {
  const allTemplatesById = useMemo(() => {
    return keyBy(Object.values(templatesByPage).flat(), 'templateId');
  }, [templatesByPage]);
  const options = useMemo(() => {
    return Object.entries(templatesByPage).map(([pageType, templates]) => {
      return {
        label: (
          <FormattedMessage
            id={`page_template.type.${snakeCase(pageType)}.label`}
          />
        ),
        options: templates.map(templateAsOption),
      };
    });
  }, [templatesByPage]);
  const value = useMemo(() => {
    return templates?.map(templateAsOption) ?? [];
  }, [templates]);
  const onChange = useCallback(
    (next: Option[]) => {
      onTemplatesChange?.(
        next.map(({ value }) => allTemplatesById[value]).filter(Boolean),
      );
    },
    [allTemplatesById, onTemplatesChange],
  );

  return (
    <PopoverSelect
      title={
        <FormattedMessage id="company_page_templates.fields.source.label" />
      }
      triggerContent={
        <TriggerContent>
          <FormattedMessage id="company_page_templates.fields.source.placeholder" />
        </TriggerContent>
      }
      options={options}
      grouped
      isMulti
      value={value}
      onChange={onChange}
      checkboxColor="black"
      {...restProps}
    />
  );
};
